import React, { useCallback, useEffect, useState } from 'react';
import { Search, Users, ShieldCheck, ShieldOff, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { superAdminService } from '../../services/superadmin';
import { AcoesDaLinha, BotaoAtivar, BotaoDesativar } from '../../components/BotoesAcao';

interface UsuarioGlobal {
  id: number;
  name: string;
  email: string;
  role: string;
  ativo: boolean;
  desativado_em: string | null;
  empresa?: { id: number; nome: string; ativo: boolean } | null;
}

interface Props {
  empresas: Array<{ id: number; nome: string }>;
}

const ROTULO_PAPEL: Record<string, string> = {
  admin: 'Administrador',
  gestor: 'Gestor',
  operador: 'Operador',
  motorista: 'Motorista',
};

const UsuariosGlobais: React.FC<Props> = ({ empresas }) => {
  const [usuarios, setUsuarios] = useState<UsuarioGlobal[]>([]);
  const [stats, setStats] = useState<{ total: number; ativos: number; inativos: number } | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [filtroPapel, setFiltroPapel] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [alterando, setAlterando] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [lista, numeros] = await Promise.all([
        superAdminService.getUsuariosGlobal({
          search: busca || undefined,
          empresa_id: filtroEmpresa ? Number(filtroEmpresa) : undefined,
          role: filtroPapel || undefined,
          ativo: filtroStatus === '' ? undefined : filtroStatus === 'ativo',
          per_page: 200,
        }),
        superAdminService.getUsuariosStats(),
      ]);
      setUsuarios(lista.data || []);
      setStats(numeros.data || null);
    } catch (e: any) {
      // Sem isto, uma falha de rede deixava a tela vazia e indistinguível de
      // "nenhum usuário encontrado".
      setErro(e?.response?.data?.message || 'Não foi possível carregar os usuários.');
    } finally {
      setCarregando(false);
    }
  }, [busca, filtroEmpresa, filtroPapel, filtroStatus]);

  useEffect(() => {
    const t = setTimeout(carregar, 300); // evita uma requisição por tecla digitada
    return () => clearTimeout(t);
  }, [carregar]);

  const alternarStatus = async (usuario: UsuarioGlobal) => {
    if (alterando) return;
    const desativando = usuario.ativo;
    const aviso = desativando
      ? `Desativar ${usuario.name}? A pessoa perde o acesso imediatamente e as sessões abertas são encerradas.`
      : `Reativar ${usuario.name}?`;
    if (!window.confirm(aviso)) return;

    setAlterando(usuario.id);
    try {
      const res = await superAdminService.toggleUsuarioStatus(usuario.id);
      toast.success(res.message || 'Status alterado');
      setUsuarios((atual) =>
        atual.map((u) => (u.id === usuario.id ? { ...u, ativo: !u.ativo } : u)),
      );
      superAdminService.getUsuariosStats().then((n: any) => setStats(n.data)).catch(() => {});
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Não foi possível alterar o status.');
    } finally {
      setAlterando(null);
    }
  };

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm"><Users className="h-4 w-4" /> Usuários</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm"><ShieldCheck className="h-4 w-4 text-green-600" /> Ativos</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{stats.ativos}</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm"><ShieldOff className="h-4 w-4 text-red-500" /> Desativados</div>
            <div className="text-2xl font-bold text-red-500 mt-1">{stats.inativos}</div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <label htmlFor="busca-usuario" className="sr-only">Buscar usuário</label>
            <input
              id="busca-usuario"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome ou e-mail"
              className="pl-9 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="filtro-empresa" className="sr-only">Empresa</label>
            <select id="filtro-empresa" value={filtroEmpresa} onChange={(e) => setFiltroEmpresa(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">Todas as empresas</option>
              {empresas.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={filtroPapel} onChange={(e) => setFiltroPapel(e.target.value)} aria-label="Perfil"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">Perfil</option>
              {Object.entries(ROTULO_PAPEL).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
            </select>
            <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} aria-label="Situação"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">Situação</option>
              <option value="ativo">Ativos</option>
              <option value="inativo">Desativados</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        {erro ? (
          <div role="alert" className="p-6 text-center">
            <p className="text-red-600 mb-3">{erro}</p>
            <button onClick={carregar} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Tentar novamente
            </button>
          </div>
        ) : carregando ? (
          <div className="p-10 text-center text-gray-500">Carregando usuários...</div>
        ) : usuarios.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            Nenhum usuário encontrado com os filtros atuais.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Usuário</th>
                  <th className="text-left px-4 py-3 font-semibold">Empresa</th>
                  <th className="text-left px-4 py-3 font-semibold">Perfil</th>
                  <th className="text-left px-4 py-3 font-semibold">Situação</th>
                  <th className="text-right px-4 py-3 font-semibold">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {usuarios.map((u) => (
                  <tr key={u.id} className={u.ativo ? '' : 'bg-red-50/40'}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{u.name}</div>
                      <div className="text-gray-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-gray-700">
                        <Building2 className="h-3.5 w-3.5 text-gray-400" />
                        {u.empresa?.nome || '—'}
                      </div>
                      {u.empresa && !u.empresa.ativo && (
                        <span className="text-xs text-amber-700">empresa desativada</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{ROTULO_PAPEL[u.role] || u.role}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        u.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {u.ativo ? 'Ativo' : 'Desativado'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <AcoesDaLinha>
                        {u.ativo
                          ? <BotaoDesativar onClick={() => alternarStatus(u)} disabled={alterando === u.id} />
                          : <BotaoAtivar onClick={() => alternarStatus(u)} disabled={alterando === u.id} />}
                      </AcoesDaLinha>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsuariosGlobais;
